/**
 * The button, and the silent dot on it — level one of D1.
 *
 * Nothing blocks, nothing moves, nothing disappears by itself. Somebody who is in a hurry sees
 * a dot; somebody who is curious opens a panel. That is the whole interaction.
 *
 * ## The dot is not the only information
 *
 * It carries a count in its `aria-label` ("What's New, 2 new entries") and a `title` for the
 * pointer. A small coloured circle is invisible to a screen reader and ambiguous to everyone
 * else, and this app has nineteen users of whom several will meet it for the first time in the
 * minute before a customer call.
 *
 * ## Before the state has loaded, and after it failed, there is no dot
 *
 * `ready` is false until the one round trip has settled, and the failure case answers with an
 * empty set (D9). The button is there either way — the history is in the bundle and can always
 * be read — but a dot only ever appears for something that is really unseen.
 */

import { Sparkles } from 'lucide-react';
import { useWhatsNew } from './WhatsNewProvider';

interface Props {
    /** Extra classes for the host's own toolbar rhythm. The module styles the dot, not the bar. */
    className?: string;
}

export function WhatsNewButton({ className = '' }: Props) {
    const { ready, unseenEntries, panelOpen, openPanel, closePanel } = useWhatsNew();

    const count = ready ? unseenEntries.length : 0;
    const label =
        count === 0
            ? "What's New"
            : `What's New, ${count} new ${count === 1 ? 'entry' : 'entries'}`;

    return (
        <button
            type="button"
            onClick={panelOpen ? closePanel : openPanel}
            aria-label={label}
            aria-expanded={panelOpen}
            title={label}
            className={`relative inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 ${className}`}
        >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            What&apos;s New
            {count > 0 && (
                <span
                    className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-blue-500"
                    aria-hidden="true"
                />
            )}
        </button>
    );
}
